import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { deliveryHistoryHeader2 } from "../data/tableData";
import Dropdown from "../components/Dropdown";
import useDataFetch from "../hooks/useDataFetch";
import DriverDeliveryHistoryTable2 from "../components/DriverDeliveryHistoryTable2";
import { MobileResponsive } from "./MobileResponsive";
import PrimaryButton from "../components/PrimaryButton";
import { updatePointsService } from "../features/driverSlice";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";
import { fetchOrdersApi } from "../api/services/orderService";

const PAGE_SIZE = 15;

const timeToParam = (label) => {
  switch (label) {
    case "Last 3 days": return 3;
    case "Last 7 days": return 7;
    case "Last 30 days": return 30;
    case "Last 90 days": return 90;
    default: return "all";
  }
};

const DriversHistory = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { data, isLoading, mutate } = useDataFetch(
    `/admin/driver-detail/${id}`
  );

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTime, setSelectedTime] = useState("Last 90 days");
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [points, setPoints] = useState(0);

  const timeOptions = [
    "Last 3 days",
    "Last 7 days",
    "Last 30 days",
    "Last 90 days",
    "All time",
  ];

  const [sidebars, setSidebars] = useState({ editPoints: false });
  const sidebarRefs = { editPoints: useRef(null) };

  const toggleSidebar = (key, value) => {
    setSidebars((prev) => ({ ...prev, [key]: value }));
  };

  const handleCloseSidebar = () => {
    setSidebars({ editPoints: false });
  };

  // Fetch orders from new API
  useEffect(() => {
    setOrdersLoading(true);
    fetchOrdersApi({
      driverId: id,
      page: currentPage,
      time: timeToParam(selectedTime),
      orderId: activeSearch || undefined,
    })
      .then((res) => {
        setOrders(res.orders || []);
        setTotalCount(res.total || 0);
      })
      .catch(() => {
        setOrders([]);
        setTotalCount(0);
      })
      .finally(() => setOrdersLoading(false));
  }, [id, currentPage, selectedTime, activeSearch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTime, activeSearch]);

  useEffect(() => {
    if (data?.data?.points !== undefined) {
      setPoints(data.data.points);
    }
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.keys(sidebarRefs).some((key) =>
          sidebarRefs[key]?.current?.contains(event.target)
        )
      ) {
        handleCloseSidebar();
      }
    };
    if (Object.values(sidebars).some((val) => val)) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebars]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setActiveSearch(searchInput.trim());
  };

  const handleUpdatePoints = async () => {
    const resultAction = await dispatch(
      updatePointsService({ driverId: id, points: parseInt(points) })
    );
    if (updatePointsService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      mutate();
      setSidebars({ editPoints: false });
    }
  };

  const handleIncrement = () =>
    setPoints((p) => parseInt(p, 10) + 10);

  const handleDecrement = () =>
    setPoints((p) => Math.max(parseInt(p, 10) - 10, 0));

  if (isLoading) {
    return (
      <>
        <MobileResponsive />
        <div className="lg:flex hidden items-start">
          <Sidebar />
          <div className="ml-[14.5vw] w-full h-screen flex items-center justify-center bg-gray-50">
            <div className="flex flex-col items-center gap-2">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Comprehensive Order History Overview"
        pageDescription="Enhance your delivery operations with Beep! Manage new driver requests, view delivery history, track driver performance, and monitor weekly earnings. Suspend or delete drivers effortlessly."
        pageKeywords="Beep, driver management, new driver requests, delivery history, driver performance, suspend driver, delete driver, weekly earnings"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        {Object.values(sidebars).some((isOpen) => isOpen) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 transition-opacity duration-500 ease-in-out"></div>
        )}

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between w-full">
            <Link to={`/drivers/${id}`} className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div>
              <span className="font-semibold text-base">Driver history</span>
            </Link>

            <div className="flex items-center">
              <form
                className="flex items-center gap-2 rounded-full px-3 border-2 border-[#F7631D] w-[27vw]"
                onSubmit={handleSearchSubmit}
              >
                <div className="relative w-full">
                  <input
                    type="text"
                    placeholder="Search by order ID"
                    className="peer rounded-full py-1.5 text-[#F7631D] placeholder:text-[#F7631D]/60 text-base font-medium w-full outline-none px-1"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <button type="submit">
                  <img src="/assets/search.svg" alt="Search" />
                </button>
              </form>
            </div>

            <div className="flex items-center gap-3">
              <Dropdown
                options={timeOptions}
                selected={selectedTime}
                onSelect={setSelectedTime}
                label="Select time"
              />
              <button
                className="bg-quinary text-white rounded-full px-3 pt-0.5 pb-1 text-sm font-medium tracking-wide"
                onClick={() => toggleSidebar("editPoints", true)}
              >
                Edit points
              </button>
            </div>
          </div>

          <div className="w-full rounded-lg flex flex-col items-center justify-between gap-4 py-8">
            {ordersLoading ? (
              <div className="flex items-center justify-center w-full py-20">
                <div className="h-10 w-10 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              </div>
            ) : (
              <div className="flex flex-col gap-5 w-full">
                <DriverDeliveryHistoryTable2
                  headers={deliveryHistoryHeader2}
                  data={orders}
                  currentPage={currentPage}
                  totalCount={totalCount}
                  pageSize={PAGE_SIZE}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>

          {/* Edit Driver's Points Sidebar */}
          <div
            ref={sidebarRefs.editPoints}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.editPoints ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>

              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold 2xl:text-[18px] xl:text-[18px] lg:text-[16px]">
                    Edit driver's points
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                <Avatar
                  name={data?.data?.name}
                  size="84"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.data?.name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="text-[18px] font-semibold w-full">
                  {data?.data?.phone}
                </p>
                <div className="w-full flex justify-end items-center">
                  <PrimaryButton
                    text={"Call Driver"}
                    path={`tel:${data?.data?.phone}`}
                    width="w-[60%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <p className="text-lg">Edit the driver's points here</p>
              </div>

              <div className="flex justify-between items-center space-x-4 w-[90%] px-[4px] py-5 rounded-lg">
                <div className="flex items-center gap-3">
                  <label>Subtract 10 points</label>
                </div>
                <div className="flex items-center gap-5">
                  <button
                    className="w-7 h-7 flex justify-center items-center rounded-full bg-[#A7A7A7] text-white hover:bg-[#b8b8b8] pb-1"
                    onClick={handleDecrement}
                  >
                    –
                  </button>
                  <input
                    type="number"
                    value={points}
                    min="0"
                    onChange={(e) => setPoints(e.target.value)}
                    className="w-24 h-12 border-2 border-orange-400 rounded-md text-2xl text-orange-500 font-bold text-center"
                  />
                  <button
                    className="w-7 h-7 flex justify-center items-center rounded-full bg-orange-500 text-white hover:bg-orange-600"
                    onClick={handleIncrement}
                  >
                    &#x271B;
                  </button>
                </div>
                <div className="flex items-center text-end gap-3">
                  <label>Add 10 points</label>
                </div>
              </div>

              <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-5">
                <PrimaryButton
                  text={"Submit"}
                  width="w-full"
                  uppercase={false}
                  event={handleUpdatePoints}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DriversHistory;
